/**
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 */

import { useTranslation } from "react-i18next"
import DividerWithText from "../../../../../components/DividerWithText"
import CustomRadio from "../../../../../components/CustomRadio"
import ManageContractorsApp from "../../Functionality/ManageContractorsApp"
import { CustomDateRangePicker } from "../../../../../components/CustomDateRangePicker"
import { CustomSelect } from "../../../../../components/CustomSelect"
import { CustomTextField } from "../../../../../components/CustomTextField"
import CustomRadioGroup from "../../../../../components/CustomRadioGroup"
import { forwardRef } from 'react';
import { FREQUENCY_RENEWAL } from '../../../../../enums/FrequencyRenewalEnum';
import {USER_TYPE_MODEL} from '../../../../../enums/userTypeEnum';

const ManageContractors = forwardRef((props, ref) => {
  const { userType} = props;

  const { t } = useTranslation();
  const {
    config,
    onChange,
    setConfig,
    TIME_UNIT_OPTIONS_TRANS,
    txtLadDays,
    txtFrequencyValue,
    emptyFields, emptyFieldsCustom,
    isValidFrequency,
    isValidLeadDays,
  } = ManageContractorsApp({
      ref,
      userType
  });

  return (
    <div className="px-3">
      <DividerWithText title={t("documentation_renewal")} spacing="pb-3" />

      <p className="font-color-shades-950 fs-14px ibl-font-w-600">
        {t("frequency_for_renewal")}
      </p>
      <CustomRadioGroup className="col-12">
        <CustomRadio
          id="rbtn_mc_one_time"
          trans="one_time_only"
          value={FREQUENCY_RENEWAL.ONE_TIME_ONLY}
          name="renewal"
          checked={config.renewal === FREQUENCY_RENEWAL.ONE_TIME_ONLY}
          onChange={onChange}
          labelClassName="fs-11px"
        />

        <div className="row g-2 align-items-center col-12 p-0">
          <div className="col-md-auto p-0 w-200px">
            <CustomRadio
              id="rbtn_mc_custom"
              trans="custom_interval"
              value={FREQUENCY_RENEWAL.CUSTOM_INTERVAL}
              name="renewal"
              checked={config.renewal === FREQUENCY_RENEWAL.CUSTOM_INTERVAL}
              onChange={onChange}
              labelClassName="fs-11px"
            />
          </div>
          <div className="col-12 col-md-3 pl-0 ml-md-5 mb-1">
            <CustomTextField
              id="txt_mc_frequency_value"
              trans="frequency_value"
              type="number"
              name="frequencyValue"
              value={config.frequencyValue}
              onChange={onChange}
              required={config?.renewal === FREQUENCY_RENEWAL.CUSTOM_INTERVAL}
              disabled={config?.renewal === FREQUENCY_RENEWAL.ONE_TIME_ONLY}
              length={3}
              placeholder={t("enter_a_number")}
              isValidText={txtFrequencyValue}
              isValid={isValidFrequency}
              emptyFields={emptyFieldsCustom && config?.renewal === FREQUENCY_RENEWAL.CUSTOM_INTERVAL}
            />
          </div>
          <div className="col-12 col-md-3 pl-0 mb-2">
            <CustomSelect
              id="slt_mc_time_unit"
              name="timeUnit"
              keyName="key"
              trans="time_unit"
              required={config?.renewal === FREQUENCY_RENEWAL.CUSTOM_INTERVAL}
              value={config?.timeUnit}
              onChange={onChange}
              list={TIME_UNIT_OPTIONS_TRANS}
              showNoSelectionOption={false}
              disabled={config?.renewal === FREQUENCY_RENEWAL.ONE_TIME_ONLY}
            />
          </div>
        </div>
      </CustomRadioGroup>

      <p className="font-color-shades-950 fs-14px ibl-font-w-600">
        {t("validity_period")}
      </p>

      <div className="row g-2 align-items-center">
        <div className="col-12 col-md-auto d-flex align-items-center p-0 w-200px fs-12px">
          <p className="mt-md-0 mt-3">{t("start_date_and_end_date")}</p>
        </div>
        <div className="col-12 col-md-3 pt-2 pt-3 pl-0 ml-md-5">
          <CustomDateRangePicker
            id="dp_mc_custom"
            disabled={false}
            required={true}
            trans="date_range"
            value={config}
            setRangeDate={setConfig}
            endDateIndeterminate={config?.renewal === FREQUENCY_RENEWAL.ONE_TIME_ONLY}
            textIndeterminate={t("forever")}
            minDate={new Date()}
          />
        </div>
      </div>

      <p className="font-color-shades-950 fs-14px ibl-font-w-600 mt-3 mt-md-0">
        {t("lead_days")}
      </p>

      <div className="row g-2 align-items-center">
        <div className="col-12 col-md-auto d-flex align-items-center p-0 w-200px fs-12px">
          <p className="mt-md-0 mt-3">{`${t("upload_allowed_before")}`}</p>
        </div>
        <div className="col-12 col-md-3 pt-2 pt-3 pl-0 ml-md-5">
          <CustomTextField
            id="txt_mc_number_days_before"
            type="number"
            trans={"quantity"}
            name="leadDays"
            value={config.leadDays}
            onChange={onChange}
            required={true}
            length={3}
            placeholder={t("number_of_days_before_the_start_date")}
            isValidText={txtLadDays}
            isValid={isValidLeadDays}
            emptyFields={emptyFields}
          />
        </div>
      </div>

      <DividerWithText title={t("deletion_policy")} spacing="pb-3" />
      <div className="row g-2 align-items-center">
        <p className="mt-md-0 mt-3 font-color-shades-950 fs-14px ibl-font-w-600 ">
            {userType === USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE ?
                t("inactive_contractor_retention") : t("inactive_supplier_retention")}
        </p>
      </div>
      <div className="row g-2 align-items-center">
        <div className="col-12 col-md-auto d-flex align-items-start p-0 w-200px">
          <p className="font-color-shades-950 fs-12px ibl-font-w-400">
              {userType === USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE ?
                  t("set_the_number_of_days_before_an_inactive_contractor_is_deleted") : t("set_the_number_of_days_before_an_inactive_supplier_is_deleted")}
          </p>
        </div>
        <div className="col-12 col-md-3 pt-2 pt-3 pl-0 ml-md-5">
          <CustomTextField
            id="txt_mc_wait_time"
            type="number"
            trans={"quantity"}
            name="waitTime"
            value={config?.waitTime}
            onChange={onChange}
            required
            disabled={false}
            length={3}
            placeholder={t("number_of_days_before_the_start_date")}
            emptyFields={emptyFields}
          />
        </div>
      </div>

    </div>
  );
});

export default ManageContractors;
